import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, Clock, User, Search } from "lucide-react";
import { Container, PageHero, Reveal, SectionHeading } from "../components/shared/Shared";
import { cn } from "../lib/utils";
import { useLang } from "../context/LanguageContext";

export const Works = () => {
  const { t, c } = useLang();
  const w = t.works;
  const cats = useMemo(() => [w.all, ...Array.from(new Set(c.WORKS.map((x) => x.category)))], [c, w.all]);
  const [cat, setCat] = useState(w.all);
  const active = cats.includes(cat) ? cat : w.all;
  const list = active === w.all ? c.WORKS : c.WORKS.filter((x) => x.category === active);
  return (
    <>
      <PageHero title={w.title} subtitle={w.subtitle} crumbs={[{ label: w.title }]} image={c.IMAGES.servers} />
      <section className="py-20" data-testid="works-page">
        <Container>
          <div className="flex flex-wrap gap-2" data-testid="works-filters">
            {cats.map((x) => <button key={x} type="button" onClick={() => setCat(x)} className={cn("rounded-full border px-4 py-2 text-sm font-semibold transition-colors", active === x ? "border-brand-teal bg-brand-teal text-white" : "border-slate-200 bg-white text-slate-600 hover:border-brand-teal hover:text-brand-teal")}>{x}</button>)}
          </div>
          <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {list.map((x, i) => (
              <Reveal key={x.title} delay={(i % 3) * 80} className="card-hover group flex flex-col overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-100" data-testid="work-card">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={x.image} alt={x.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <span className="absolute start-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-brand-navy backdrop-blur">{x.category}</span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center gap-4 text-xs text-slate-500"><span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />{w.byAdmin}</span><span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{x.date}</span></div>
                  <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-brand-navy transition-colors group-hover:text-brand-teal">{x.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{x.summary}</p>
                  <Link to="/contact" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand-orange">{w.discuss} <ArrowUpRight className="h-4 w-4 rtl:-scale-x-100" /></Link>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>
    </>
  );
};

export const Blogs = () => {
  const { t, c } = useLang();
  const b = t.blogs;
  const [q, setQ] = useState("");
  const list = c.BLOGS.filter((x) => (x.title + x.category + x.excerpt).toLowerCase().includes(q.toLowerCase()));
  const [featured, ...rest] = list;
  return (
    <>
      <PageHero title={b.title} subtitle={b.subtitle} crumbs={[{ label: t.nav.blogs }]} image={c.IMAGES.heroAlt} />
      <section className="py-20" data-testid="blogs-page">
        <Container>
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <SectionHeading eyebrow={b.latest} title={b.heading} />
            <label className="relative block w-full max-w-sm">
              <Search className="pointer-events-none absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={b.searchPh} data-testid="blog-search-input" className="h-12 w-full rounded-xl border border-slate-200 bg-white ps-11 pe-4 text-sm outline-none transition-colors focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20" />
            </label>
          </div>
          {featured ? (
            <>
              <Reveal className="card-hover group mt-12 grid overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-100 lg:grid-cols-2">
                <div className="overflow-hidden"><img src={featured.image} alt={featured.title} className="h-full min-h-[280px] w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                <div className="flex flex-col justify-center p-8 lg:p-12">
                  <span className="w-fit rounded-full bg-brand-orange/10 px-3 py-1 text-xs font-bold text-brand-orange">{featured.category}</span>
                  <h3 className="mt-5 font-heading text-3xl font-bold leading-tight text-brand-navy">{featured.title}</h3>
                  <p className="mt-4 leading-relaxed text-slate-600">{featured.excerpt}</p>
                  <div className="mt-6 flex items-center gap-4 text-xs text-slate-500"><span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{featured.date}</span><span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{featured.readTime}</span></div>
                </div>
              </Reveal>
              <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {rest.map((x, i) => (
                  <Reveal key={x.title} delay={(i % 3) * 80} className="card-hover group flex flex-col overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-100" data-testid="blog-card">
                    <div className="aspect-[16/10] overflow-hidden"><img src={x.image} alt={x.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" /></div>
                    <div className="flex flex-1 flex-col p-6">
                      <span className="w-fit rounded-full bg-brand-teal/10 px-3 py-1 text-xs font-bold text-brand-teal">{x.category}</span>
                      <h3 className="mt-4 font-heading text-lg font-bold leading-snug text-brand-navy transition-colors group-hover:text-brand-teal">{x.title}</h3>
                      <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{x.excerpt}</p>
                      <div className="mt-5 flex items-center gap-4 text-xs text-slate-500"><span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{x.date}</span><span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{x.readTime}</span></div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </>
          ) : <p className="mt-12 text-center text-slate-500">{b.none}</p>}
        </Container>
      </section>
    </>
  );
};
